import { useEffect, useState } from 'react';
import { useProductPricesSub } from '../../../hooks/useProductPricesSub';
import { ProductType } from '../../../hooks/useProducts';
import { useHasSlTpError } from '../../../hooks/useSlTpHooks';
import { calcPctFromPrice, calcPriceFromPct } from '../../../lib/calcPriceFromPct';
import { cn } from '../../../lib/classNameHelpers';
import { Gwei } from '../../../lib/gwei';
import { getDecimalPlaces } from '../../../lib/math';
import { OrderSide } from '../../../types/generated';
import { Input } from '../../Input/Input';
import { TpSlError } from '../../TpSlError/TpSlError';
import { useTradeFormStore } from './TradeFormState';

interface TradeFormTpSlUiProps {
  product: ProductType;
  TpSlError: typeof TpSlError;
  hasStopLossError: boolean;
  hasTakeProfitError: boolean;
  oraclePrice: bigint;
}

export const TradeFormTpSlUi: React.FC<TradeFormTpSlUiProps> = ({
  product,
  TpSlError,
  hasStopLossError,
  hasTakeProfitError,
  oraclePrice,
}) => {
  const [takeProfitPct, setTakeProfitPct] = useState<bigint | undefined>();
  const [stopLossPct, setStopLossPct] = useState<bigint | undefined>();

  const orderType = useTradeFormStore((state) => state.orderType);
  const limitPrice = useTradeFormStore((state) => state.limitPrice);
  const showTpSl = useTradeFormStore((state) => state.showTpSl);
  const stopLoss = useTradeFormStore((state) => state.stopLoss);
  const takeProfit = useTradeFormStore((state) => state.takeProfit);
  const setStopLoss = useTradeFormStore((state) => state.setStopLoss);
  const setTakeProfit = useTradeFormStore((state) => state.setTakeProfit);
  const side = useTradeFormStore((state) => state.side);

  const avgPrice = orderType === 'LIMIT' ? (limitPrice ?? 0n) : oraclePrice;

  useEffect(() => {
    setStopLoss(undefined);
    setTakeProfit(undefined);
    setTakeProfitPct(undefined);
    setStopLossPct(undefined);
  }, [showTpSl, setStopLoss, setTakeProfit]);

  if (!showTpSl) return null;
  const priceDecimals = getDecimalPlaces(product.tickSize);
  return (
    <div className="flex flex-col gap-2">
      {/* Take Profit */}
      <div className="flex gap-2">
        <Input
          name="takeProfit"
          aria-label="Take Profit"
          aria-describedby={'takeProfit'}
          aria-required="true"
          aria-errormessage={hasTakeProfitError ? 'take-profit-error' : undefined}
          inputType="number"
          label="Take Profit"
          inputMode="numeric"
          step={Gwei.fmt(product.tickSize)}
          isAllowed={(values) => {
            const { floatValue = 0 } = values;
            return floatValue < Number(Gwei.fmt(product.maxPrice));
          }}
          min={Number(Gwei.fmt(product.minPrice))}
          decimalScale={priceDecimals}
          value={takeProfit !== undefined ? `${Gwei.fmt(takeProfit)}` : ''}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source !== 'event') return;
            if (values.value === '') {
              setTakeProfit(undefined);
              setTakeProfitPct(undefined);
              return;
            }

            const price = Gwei.toGwei(values.value);
            const pct = calcPctFromPrice({
              price,
              avgPrice,
              side: side === 'long' ? OrderSide.BUY : OrderSide.SELL,
            });
            setTakeProfitPct(pct);
            setTakeProfit(price);
          }}
          containerClassName={cn('flex-1', hasTakeProfitError ? 'border border-borderError' : '')}
          hasError={hasTakeProfitError}
        />
        <Input
          label="Gain"
          inputType="number"
          containerClassName="flex-1"
          aria-label="Take Profit Percentage"
          allowNegative={true}
          decimalScale={2}
          rightElement={<span className="text-xs font-bold text-contentSecondary">%</span>}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source !== 'event') return;
            if (values.value === '') {
              setTakeProfitPct(undefined);
              setTakeProfit(undefined);
              return;
            }

            const percent = Gwei.toGwei(values.value);
            setTakeProfitPct(percent);

            const calculatedPrice = calcPriceFromPct({
              percent,
              avgPrice,
              tickSize: product.tickSize,
              side: side === 'long' ? OrderSide.BUY : OrderSide.SELL,
              isStopLoss: false,
            });
            setTakeProfit(calculatedPrice);
          }}
          value={takeProfitPct === undefined || hasTakeProfitError ? '' : Gwei.fmt(takeProfitPct)}
        />
      </div>
      <TpSlError
        stopLossPrice={stopLoss}
        takeProfitPrice={takeProfit}
        side={side}
        tpOrSl="tp"
        productId={product.id}
      />
      {/* Stop Loss */}
      <div className="flex gap-2">
        <Input
          name="stopLoss"
          aria-label="Stop Loss"
          aria-describedby={'stopLoss'}
          aria-required="true"
          aria-errormessage={hasStopLossError ? 'stop-loss-error' : undefined}
          inputMode="numeric"
          label="Stop Loss"
          inputType="number"
          value={stopLoss !== undefined ? Gwei.fmt(stopLoss) : ''}
          step={Gwei.fmt(product.tickSize)}
          isAllowed={(values) => {
            const { floatValue = 0 } = values;
            return floatValue < Number(Gwei.fmt(product.maxPrice));
          }}
          min={Number(Gwei.fmt(product.minPrice))}
          decimalScale={priceDecimals}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source !== 'event') return;
            if (values.value === '') {
              setStopLoss(undefined);
              setStopLossPct(undefined);
              return;
            }
            const price = Gwei.toGwei(values.value);

            const pct = calcPctFromPrice({
              price,
              avgPrice,
              side: side === 'long' ? OrderSide.SELL : OrderSide.BUY,
            });
            setStopLossPct(pct);
            setStopLoss(price);
          }}
          hasError={hasStopLossError}
          containerClassName={cn('flex-1', hasStopLossError ? 'border border-borderError' : '')}
        />
        <Input
          label="Loss"
          inputType="number"
          containerClassName="flex-1"
          aria-label="Stop Loss Percentage"
          allowNegative={true}
          decimalScale={2}
          rightElement={<span className="text-xs font-bold text-contentSecondary">%</span>}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source !== 'event') return;
            if (values.value === '') {
              setStopLossPct(undefined);
              setStopLoss(undefined);
              return;
            }

            const percent = Gwei.toGwei(values.value);
            setStopLossPct(percent);

            const calculatedPrice = calcPriceFromPct({
              percent,
              avgPrice,
              tickSize: product.tickSize,
              side: side === 'long' ? OrderSide.BUY : OrderSide.SELL,
              isStopLoss: true,
            });
            setStopLoss(calculatedPrice);
          }}
          value={stopLossPct === undefined || hasStopLossError ? '' : Gwei.fmt(stopLossPct)}
        />
      </div>
      <div className="pb-2">
        <TpSlError
          stopLossPrice={stopLoss}
          takeProfitPrice={takeProfit}
          side={side}
          tpOrSl="sl"
          productId={product.id}
        />
      </div>
    </div>
  );
};

export const TradeFormTpSl = ({ product }: { product: ProductType }) => {
  const stopLossPrice = useTradeFormStore((state) => state.stopLoss);
  const takeProfitPrice = useTradeFormStore((state) => state.takeProfit);
  const side = useTradeFormStore((state) => state.side);

  const { data: pricesByProductId } = useProductPricesSub();
  const oraclePrice = pricesByProductId?.[product.id]?.oraclePrice ?? 0n;

  const { hasStopLossError, hasTakeProfitError } = useHasSlTpError({
    side,
    stopLossPrice,
    takeProfitPrice,
    productId: product.id,
  });
  return (
    <TradeFormTpSlUi
      product={product}
      TpSlError={TpSlError}
      oraclePrice={oraclePrice}
      hasStopLossError={hasStopLossError}
      hasTakeProfitError={hasTakeProfitError}
    />
  );
};
